import React, { useEffect, useState } from 'react';
import api from '../../../adapters/axios'; // Adjust path as necessary
import '../AlertMenu/AlertMenu';

interface Subscription {
  type: 'Tag' | 'Company';
  name: string;
  frequency: 'Daily' | 'Weekly' | 'Never';
}

const AlertMenu = () => {
  const [tags, setTags] = useState<string[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<'Never' | 'Daily' | 'Weekly'>('Never');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  useEffect(() => {
    fetchTags();
    fetchCompanies();
    fetchSubscriptions();
  }, []);

  const fetchTags = async () => {
    try {
      const res = await api.get('/getTags');
      const data = res.data;
      if (!Array.isArray(data)) throw new Error('Invalid tag response');
      setTags(data.map((t: any) => t.tagName));
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/getCompanies');
      const data = res.data;
      if (!Array.isArray(data)) throw new Error('Invalid company response');
      setCompanies(data.map((c: any) => c.companyName));
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await api.get('/email-alerts');
      const data = res.data;
      if (!Array.isArray(data)) throw new Error('Invalid subscriptions');
      setSubscriptions(data);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    }
  };

  const handleFrequencyChange = async (
    sub: Subscription,
    newFrequency: 'Never' | 'Daily' | 'Weekly'
  ) => {
    try {
      await api.patch('/email-alerts/frequency', {
        type: sub.type,
        name: sub.name,
        frequency: newFrequency,
      });
      fetchSubscriptions();
    } catch (err) {
      console.error('Error updating frequency:', err);
    }
  };

  const handleUnsubscribe = async (sub: Subscription) => {
    try {
      await api.delete('/email-alerts/unsubscribe', {
        data: {
          type: sub.type,
          name: sub.name,
        },
      });
      fetchSubscriptions();
    } catch (err) {
      console.error('Error unsubscribing:', err);
    }
  };

  const handleSubscribe = async () => {
    const newSubs: Subscription[] = [
      ...selectedTags.map(tag => ({
        type: 'Tag' as const,
        name: tag,
        frequency,
      })),
      ...selectedCompanies.map(company => ({
        type: 'Company' as const,
        name: company,
        frequency,
      })),
    ];

    try {
      await api.post('/email-alerts/subscribe', newSubs);
      await fetchSubscriptions();
      setSelectedTags([]);
      setSelectedCompanies([]);
      setFrequency('Never');
    } catch (err) {
      console.error('Error subscribing:', err);
    }
  };

  return (
    <div className="alert-page-wrapper">
      <h2>🔔 <span className="alert-heading">Alert Notifications</span></h2>

      <div className="alert-form">
        {/* Tags */}
        <div className="form-group" style={{ position: 'relative' }}>
          <label>Choose Tags</label>
          <input
            type="text"
            placeholder="Search tags..."
            className="styled-select small-input"
            value={tagSearch}
            onFocus={() => setShowTagDropdown(true)}
            onChange={(e) => {
              setTagSearch(e.target.value);
              setShowTagDropdown(true);
            }}
            onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
          />
          {showTagDropdown && (
            <div className="dropdown-list">
              {tags
                .filter(tag =>
                  tag.toLowerCase().includes(tagSearch.toLowerCase()) &&
                  !selectedTags.includes(tag)
                )
                .map(tag => (
                  <div
                    key={tag}
                    className="dropdown-item"
                    onMouseDown={() => {
                      setSelectedTags(prev => [...prev, tag]);
                      setTagSearch('');
                    }}
                  >
                    {tag}
                  </div>
                ))}
            </div>
          )}
          {selectedTags.length > 0 && (
            <div className="selected-bar">
              {selectedTags.map(tag => (
                <span key={tag} className="selected-chip">
                  {tag}
                  <button
                    className="remove-chip"
                    onClick={() =>
                      setSelectedTags(prev => prev.filter(t => t !== tag))
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Companies */}
        <div className="form-group" style={{ position: 'relative' }}>
          <label>Choose Companies</label>
          <input
            type="text"
            placeholder="Search companies..."
            className="styled-select small-input"
            value={companySearch}
            onFocus={() => setShowCompanyDropdown(true)}
            onChange={(e) => {
              setCompanySearch(e.target.value);
              setShowCompanyDropdown(true);
            }}
            onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
          />
          {showCompanyDropdown && (
            <div className="dropdown-list">
              {companies
                .filter(company =>
                  company.toLowerCase().includes(companySearch.toLowerCase()) &&
                  !selectedCompanies.includes(company)
                )
                .map(company => (
                  <div
                    key={company}
                    className="dropdown-item"
                    onMouseDown={() => {
                      setSelectedCompanies(prev => [...prev, company]);
                      setCompanySearch('');
                    }}
                  >
                    {company}
                  </div>
                ))}
            </div>
          )}
          {selectedCompanies.length > 0 && (
            <div className="selected-bar">
              {selectedCompanies.map(company => (
                <span key={company} className="selected-chip">
                  {company}
                  <button
                    className="remove-chip"
                    onClick={() =>
                      setSelectedCompanies(prev => prev.filter(c => c !== company))
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Frequency */}
        <div className="form-group">
          <label>Notification Frequency</label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as any)}
            className="styled-select small-select"
          >
            <option value="Never" className="option-blue">Never</option>
            <option value="Daily" className="option-blue">Daily</option>
            <option value="Weekly" className="option-blue">Weekly</option>
          </select>
        </div>

        {/* Subscribe Button */}
        <div className="form-group subscribe-wrapper">
          <button className="subscribe-btn" onClick={handleSubscribe}>
            🔔 Subscribe
          </button>
        </div>
      </div>

      {/* Subscription List */}
      {subscriptions.length > 0 && (
        <div className="subscription-list">
          <h3>📋 Your Subscriptions</h3>
          <table className="subscription-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Email Frequency</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub, idx) => (
                <tr key={idx}>
                  <td>{sub.type}</td>
                  <td className="text-blue">{sub.name}</td>
                  <td>
                    <select
                      value={sub.frequency}
                      onChange={(e) =>
                        handleFrequencyChange(sub, e.target.value as 'Never' | 'Daily' | 'Weekly')
                      }
                    >
                      <option value="Never">Never</option>
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                    </select>
                  </td>
                  <td>
                    <button
                      className="unsubscribe-btn blue-btn"
                      onClick={() => handleUnsubscribe(sub)}
                    >
                      Unsubscribe
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AlertMenu;
